import angular from 'angular';
import _ from 'lodash';
import Logger from '../../../shared/logger';

const FeedbackProviderCache = {};

export const PositionService = (
    Restangular,
    $localStorage,
    $q,
    EmployerService,
    EmployerRepService,
    GdprComplianceService,
    RouteService,
) => {
    const getUpdatedDiversityIds = (position, diversityIds) => {
        if (!diversityIds || !diversityIds.length || !position) {
            return;
        }

        const oldPositionData = _.get(position, 'diversityIds');
        const now = new Date();

        if (oldPositionData) {
            const { current: oldCurrent, timestamp: oldTimestamp } = oldPositionData;

            if (oldCurrent.toString() !== diversityIds.toString()) {
                return {
                    current: diversityIds,
                    timestamp: now,
                    history: [
                        ...oldPositionData.history,
                        { diversityIds: oldCurrent, timestamp: oldTimestamp },
                    ],
                };
            }

            return oldPositionData;
        }

        return {
            current: diversityIds,
            timestamp: now,
            history: [],
        };
    };

    const addPosition = function (body, edit) {
        if (edit === true) {
            const deffered = $q.defer();
            Restangular.one('position', body._id)
                .customPUT(body)
                .then((response) => {
                    if ($localStorage.positionData && $localStorage.positionData._id === body._id) {
                        for (const key in body) {
                            $localStorage.positionData[key] = body[key];
                        }
                    }
                    deffered.resolve(response);
                }, (error) => {
                    Logger.error(error);
                    deffered.reject();
                })
                .catch((error) => Logger.error(error));

            return deffered.promise;
        }
        return Restangular.all('position')
            .post(body)
            .catch((error) => Logger.error(error));
    };

    const getPosition = function (id) {
        if (id) {
            return Restangular.one('position', id)
                .get('')
                .catch((error) => Logger.error(error));
        }

        // eslint-disable-next-line no-undef
        return Promise.reject(new Error('Missing ID'));
    };

    const setPositionEvent = (body) => Restangular.all('position')
        .all('setEvent')
        .post(body)
        .catch((error) => Logger.error(error));

    const getPositions = function (body) {
        return Restangular.all('position')
            .all('search')
            .post(body)
            .catch((error) => Logger.error(error));
    };

    const changePositionStatus = function (id, body) {
        return Restangular.one('position', id)
            .all('status')
            .customPUT(body)
            .catch((error) => Logger.error(error));
    };

    const cancelClonePosition = function (body) {
        return Restangular.one('position')
            .all('cancelClone')
            .post(body)
            .catch((error) => Logger.error(error));
    };

    const clonePosition = function (body) {
        return Restangular.one('position')
            .all('clone')
            .post(body)
            .catch((error) => Logger.error(error));
    };

    const deletePosition = function (id) {
        return Restangular.one('position', id)
            .customDELETE('')
            .catch((error) => Logger.error(error));
    };

    const deletePositionFeedback = function (id, { talentId }) {
        return Restangular.one('position', id)
            .all('feedbackOnCandidate')
            .customOperation('remove', '', {}, { 'Content-Type': 'application/json' }, { talentId })
            .catch((error) => Logger.error(error));
    };

    const postPositionFeedback = function (id, data) {
        return Restangular.one('position', id)
            .all('feedbackOnCandidate')
            .post(data)
            .catch((error) => Logger.error(error));
    };

    const putPositionFeedback = function (id, data) {
        return Restangular.one('position', id)
            .all('feedbackOnCandidate')
            .customPUT(data)
            .catch((error) => Logger.error(error));
    };

    const requestFeedback = function (id, data) {
        return Restangular.one('position', id)
            .all('requestFeedback')
            .post(data)
            .catch((error) => Logger.error(error));
    };

    const removeRequestFeedback = function (id, data) {
        return Restangular.one('position', id)
            .all('removeRequestFeedback')
            .post(data)
            .catch((error) => Logger.error(error));
    };

    const updatePosition = function (id, data) {
        return Restangular.one('position', id)
            .customPUT(data)
            .catch((error) => Logger.error(error));
    };

    const getPositionComment = function (id) {
        if (id) {
            return Restangular.one('position', id)
                .all('comment')
                .get('')
                .catch((error) => Logger.error(error));
        }

        // eslint-disable-next-line no-undef
        return Promise.reject(new Error('Missing ID'));
    };

    const putPositionComment = function (id, data) {
        return Restangular.one('position', id)
            .all('comment')
            .customPUT(data)
            .catch((error) => Logger.error(error));
    };

    const getPositionReports = (id) => Restangular.one('position', id)
        .all('reports')
        .get('')
        .then(({ result }) => {
            const sorted = result.sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate));
            const [lastUpdated] = sorted;
            return lastUpdated;
        })
        .catch((error) => Logger.error(error));

    const getSelectedAndManuallySamples = async (positionId, options) => {
        const { result } = await Restangular.one('position', positionId).post('selectedAndManuallyAddedSamples', options);

        return result;
    };

    const getSelectedAndFeedbackAndManuallyAddedSamples = async (positionId, options) => {
        const { result } = await Restangular.one('position', positionId).post('selectedAndFeedbackAndManuallyAddedSamples', options);

        return result;
    };

    const getSelectedSamples = async (positionId, options) => {
        const { result } = await Restangular.one('position', positionId).post('selectedSamples', options);

        return result;
    };

    const getSelectedAndFeedbackSamples = async (positionId, options) => {
        const { result } = await Restangular.one('position', positionId).post('selectedAndFeedbackSamples', options);

        return result;
    };

    const getSelectedAndFeedbackEmployerSamples = async (positionId, options) => {
        const { result } = await Restangular.one('position', positionId).post('selectedAndFeedbackEmployerSamples', options);

        return result;
    };

    const addSample = async (positionId, talentId, candidateId, recruiterId) => {
        const { result } = await Restangular.one('position', positionId).post('addSample', {
            talentId,
            candidateId,
            recruiterId,
        });

        return result;
    };

    const getSampleActions = async (positionId, talentId) => {
        const { result } = await Restangular.one('position', positionId).one('sampleActions', talentId).get('');

        return result;
    };

    const removeSample = async (positionId, talentId) => {
        const { result } = await Restangular.one('position', positionId).one('removeSample', talentId).remove('');

        return result;
    };

    const getUploadJobCurrentWizardStep = async (positionId) => {
        const { result: response } = await Restangular.one('position', positionId).all('wizard').all('currentStep').get('');
        const taskId = _.get(response, 'taskId');

        if (!taskId) {
            return undefined;
        }

        const result = await RouteService.pollUntilTaskFinished(taskId);
        return result;
    };

    const getUploadJobWizardStep = async (positionId, params = {}) => {
        const { result: response } = await Restangular
            .one('position', positionId)
            .all('wizard')
            .all('step')
            .post({
                currentStepName: params.currentStepName,
                forward: params.forward,
                isSkipStepValidation: params.isSkipStepValidation,
            });

        const taskId = _.get(response, 'taskId');

        if (!taskId) {
            return undefined;
        }

        const result = await RouteService.pollUntilTaskFinished(taskId);
        return result;
    };

    const getFeedbackProviders = async (positionData) => {
        if (_.has(positionData, 'feedbackOnCandidates') && _.isArray(positionData.feedbackOnCandidates)) {
            const feedbackProviderIds = _.uniq(_.map(positionData.feedbackOnCandidates, 'employerRepId'));

            const { itemsToFetch } = (feedbackProviderIds || []).reduce((result, id) => {
                if (!id) {
                    return result;
                }

                if (FeedbackProviderCache[id]) {
                    result.cachedItems.push(id);
                } else {
                    result.itemsToFetch.push(id);
                }

                return result;
            }, { cachedItems: [], itemsToFetch: [] });

            if (itemsToFetch.length) {
                const [
                    { result: employers = [] },
                    { result: employerReps = [] },
                ] = await Promise.all([
                    EmployerService.getEmployersByIds(itemsToFetch),
                    EmployerRepService.getEmployersRepsByIds({ employerRepIds: itemsToFetch }),
                ]);

                for (const provider of [...employers, ...employerReps]) {
                    FeedbackProviderCache[provider._id] = provider.name;
                }
            }

            return FeedbackProviderCache;
        }

        return {};
    };

    const addRecruiterToPosition = async ({ positionId, recruiterId }) => Restangular
        .one('position', positionId)
        .one('recruiter', recruiterId)
        .all('add')
        .customPUT()
        .catch((error) => Logger.error(error));

    const sendCalibrationRequest = (positionId, requestData) => Restangular
        .one('position', positionId)
        .all('wizard')
        .all('sendCalibrationRequest')
        .post(requestData)
        .catch((error) => Logger.error(error));

    const sendFeedbackRequest = (positionId, requestData) => Restangular
        .one('position', positionId)
        .all('wizard')
        .all('sendFeedbackRequest')
        .post(requestData)
        .catch((error) => Logger.error(error));

    const changeServiceType = ({ positionId, serviceType }) => Restangular
        .one('position', positionId)
        .all('changeServiceType')
        .customPUT({ serviceType })
        .catch((error) => Logger.error(error));

    const canGenerateAutoFeedbackCandidates = async (positionId) => {
        const { result } = await Restangular
            .one('position', positionId)
            .all('wizard')
            .all('canGenerateAutoFeedbackCandidates')
            .get('')
            .catch((error) => Logger.error(error));

        return result;
    };

    const getContactChannelData = async (talentIds = [], positionId) => Restangular
        .one('position', positionId)
        .all('contact')
        .all('channel')
        .post({ talentIds });

    const checkIfPositionIsGdprCompliant = async (positionData = {}) => GdprComplianceService.checkIfPositionIsGdprCompliant({
        position: positionData,
        gdprLocations: $localStorage.gdprLocations,
        countries: $localStorage.translatedCountriesList,
    });

    return {
        addPosition,
        addRecruiterToPosition,
        updatePosition,
        getPosition,
        getPositions,
        setPositionEvent,
        changePositionStatus,
        cancelClonePosition,
        clonePosition,
        deletePosition,
        requestFeedback,
        removeRequestFeedback,
        putPositionFeedback,
        postPositionFeedback,
        deletePositionFeedback,
        getPositionReports,
        getPositionComment,
        putPositionComment,
        getSelectedSamples,
        getSelectedAndFeedbackSamples,
        getSelectedAndManuallySamples,
        getSelectedAndFeedbackEmployerSamples,
        getSelectedAndFeedbackAndManuallyAddedSamples,
        addSample,
        removeSample,
        getSampleActions,
        getUploadJobWizardStep,
        getUploadJobCurrentWizardStep,
        getFeedbackProviders,
        sendCalibrationRequest,
        changeServiceType,
        sendFeedbackRequest,
        canGenerateAutoFeedbackCandidates,
        getContactChannelData,
        getUpdatedDiversityIds,
        checkIfPositionIsGdprCompliant,
    };
};

angular
    .module('TALENYA-FE')
    .factory('PositionService', PositionService);
